import React, { useState } from 'react'
import { graphql } from '@apollo/react-hoc'
import Dropzone, { FileWithPreview } from 'react-dropzone'
import { UploadGearImageMutationFn } from '#src/generated/graphql'
import UploadGearImageDocument from '#src/components/Path/mutations/UploadGearImage.gql'
import { toast } from 'react-toastify'
import AddIcon from './add-icon.svg'
import RemoveIcon from './remove.svg'
import styles from './styles.css'
import { IdQuestionProps } from '../'

type DropzoneImage = {
  id: string
  file: FileWithPreview
}

const MediaUploadQuestionContainer: React.SFC<IdQuestionProps &
  MediaUploadQuestionContainerMutations> = ({
  onFieldValueChanged,
  onFieldValidityChanged,
  question,
  uploadGearImage
}) => {
  const dropzoneRef: React.RefObject<Dropzone> = React.createRef()
  const [dropzoneImages, setDropzoneImages] = useState<DropzoneImage[]>([])

  const handleFieldValueChange = () => {
    const dropzoneIds = dropzoneImages.map(dropzoneImage => dropzoneImage.id)

    onFieldValueChanged({ value: dropzoneIds })
  }

  const handleFieldValidity = () => {
    if (question.required) {
      if (dropzoneImages.length > 0) {
        onFieldValidityChanged(true)
      } else {
        onFieldValidityChanged(false)
      }
    } else {
      onFieldValidityChanged(true)
    }
  }

  const handleImageDropped = (accepted: FileWithPreview[]) => {
    const maybeDropzoneImages = dropzoneImages || []

    uploadGearImage!({
      variables: {
        input: {
          file: accepted[0]
        }
      }
    }).then(request => {
      if (!request) {
        toast.error('Error uploading image, please try again.')
        return null
      }

      const { data } = request

      if (data && data.uploadMedia && data.uploadMedia.mediaFile) {
        const maybeExistingImage = dropzoneImages.filter(
          dropzoneImage =>
            dropzoneImage.id === (data.uploadMedia.mediaFile ? data.uploadMedia.mediaFile.id : '')
        )

        if (maybeExistingImage.length > 0) {
          toast.error('You already uploaded this image!')
          return false
        }

        setDropzoneImages([
          ...maybeDropzoneImages,
          {
            id: data.uploadMedia.mediaFile.id,
            file: accepted[0]
          }
        ])

        handleFieldValueChange()
        handleFieldValidity()
      } else {
        toast.error('Error uploading image, please try again.')
      }
    })
  }

  const handleRemoveImage = (id: string) => {
    const updatedImages = dropzoneImages.filter(imageObject => {
      if (imageObject.id !== id) {
        return imageObject
      }
    })

    setDropzoneImages(updatedImages)
  }

  return (
    <>
      <Dropzone
        accept="image/jpeg, image/png"
        ref={() => dropzoneRef}
        acceptClassName={styles.willAcceptDrop}
        rejectClassName={styles.willRejectDrop}
        activeClassName={styles.dragIsActive}
        multiple={false}
        onDrop={handleImageDropped}
        className={styles.dropzone}
      >
        <div className={styles.emptyState}>
          <AddIcon className={styles.imageAddIcon} />
          <div className={styles.addPhotos}>Add photos</div>
          <div className={styles.instructions}>
            Drop your image here or{' '}
            <button type="button" className={styles.browse}>
              Browse files
            </button>
          </div>
          <div />
        </div>
      </Dropzone>
      <div className={styles.uploadedImages}>
        {dropzoneImages.map(dropzoneImage => {
          return (
            <div className={styles.image} key={dropzoneImage.id}>
              <button
                onClick={() => handleRemoveImage(dropzoneImage.id)}
                type="button"
                className={styles.remove}
              >
                <RemoveIcon />
              </button>
              <div className={styles.imageWrapper}>
                <img src={dropzoneImage.file.preview} />
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

interface MediaUploadQuestionContainerMutations {
  uploadGearImage?: UploadGearImageMutationFn
}

export default graphql<IdQuestionProps & MediaUploadQuestionContainerMutations>(
  UploadGearImageDocument,
  {
    name: 'uploadGearImage'
  }
)(MediaUploadQuestionContainer)
